// 📁 src/LoginPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUser } from "./UserContext";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { API_BASE } from "./apiBase";
import { useCurrency } from "./context/CurrencyContext";

// ✅ Desktop background image
import loginBgDesktop from "./assets/login-bg-desktop.png";

// Map country -> currency (keep only what you support)
const COUNTRY_TO_CURRENCY = {
  IN: "INR",
  US: "USD",
  GB: "GBP",
  AE: "AED",
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  PT: "EUR",
  IE: "EUR",
  AT: "EUR",
  FI: "EUR",
  GR: "EUR",
  LU: "EUR",
};

// ✅ Modern input class (used everywhere)
const INPUT =
  "w-full rounded-2xl bg-[#FAFAF5] px-4 py-3.5 text-[15px] " +
  "border border-transparent shadow-sm " +
  "placeholder:text-[#1E5631]/45 " +
  "focus:bg-white focus:border-[#1E5631]/20 focus:ring-2 focus:ring-[#1E5631]/15 " +
  "outline-none transition";

const onlyDigitsMax6 = (v) => String(v || "").replace(/\D/g, "").slice(0, 6);

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithEmail, sendOtp, verifyOtp } = useUser();
  const { currency, setCurrency } = useCurrency();

  const [mode, setMode] = useState("phone"); // "phone" | "email"

  // phone otp states
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  // resend cooldown
  const [cooldown, setCooldown] = useState(0);

  // email states
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });

  const [loading, setLoading] = useState(false);

  // ✅ Auto-detected country (default fallback IN)
  const [defaultCountry, setDefaultCountry] = useState("IN");

  // ✅ Only auto-set currency if user never manually chose one before
  const hasUserChosenCurrency = useMemo(() => {
    return localStorage.getItem("currency_user_set") === "1";
  }, []);

  // cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // Reset OTP flow when phone changes
  useEffect(() => {
    setOtpSent(false);
    setOtp("");
    setCooldown(0);
  }, [phone]);

  // ✅ Detect geo (country) + auto-set currency (if allowed)
  useEffect(() => {
    let ignore = false;

    const detectGeo = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/geo`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) return;
        const data = await res.json();

        const cc = (
          data?.countryCode ||
          data?.country ||
          data?.country_code ||
          "IN"
        )
          .toString()
          .toUpperCase();

        if (ignore) return;

        if (!phone) setDefaultCountry(cc);

        if (!hasUserChosenCurrency) {
          const nextCurrency = COUNTRY_TO_CURRENCY[cc];
          if (nextCurrency && nextCurrency !== currency) {
            setCurrency(nextCurrency);
          }
        }
      } catch (e) {
        // silent fail
      }
    };

    detectGeo();
    return () => {
      ignore = true;
    };
  }, [phone, currency, setCurrency, hasUserChosenCurrency]);

  const afterLogin = (userData) => {
    toast.success(`👋 Welcome, ${(userData?.name || "User").split(" ")[0]}!`);
    if (userData?.isAdmin) navigate("/admin");
    else navigate("/");
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phone) return toast.error("Please enter phone number");

    if (cooldown > 0) return;

    setLoading(true);
    try {
      await sendOtp(phone, "login");
      setOtpSent(true);
      setCooldown(30);
      toast.success("✅ OTP sent");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    const cleanOtp = onlyDigitsMax6(otp);
    if (!phone || cleanOtp.length !== 6) {
      return toast.error("Please enter 6-digit OTP");
    }

    setLoading(true);
    try {
      const userData = await verifyOtp(phone, cleanOtp);
      afterLogin(userData);
    } catch (err) {
      toast.error(err?.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await loginWithEmail(
        emailForm.email.trim(),
        emailForm.password
      );
      afterLogin(userData);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const resetOtpState = () => {
    setOtpSent(false);
    setOtp("");
    setCooldown(0);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF5] text-[#1E5631] relative overflow-hidden">
      {/* ✅ Desktop background image (lg and up) */}
      <div
        className="hidden lg:block absolute inset-0"
        style={{
          backgroundImage: `url(${loginBgDesktop})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* ✅ Desktop readability overlay */}
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-transparent via-[#FAFAF5]/70 to-[#FAFAF5]/95" />

      {/* ✅ Mobile soft background accents */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#1E5631]/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-[#D4AF57]/12 blur-3xl" />
      </div>

      <div className="relative min-h-screen flex items-center justify-center lg:justify-end px-4 py-10 lg:pr-16">
        <div className="w-full max-w-md sm:max-w-lg lg:max-w-md">
          <div className="rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] bg-white overflow-hidden">
            <div className="p-6 sm:p-10">
              {/* Header (mobile) */}
              <div className="lg:hidden mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-[#1E5631] text-white flex items-center justify-center font-bold">
                    NF
                  </div>
                  <div>
                    <div className="text-xl font-semibold">The Nutrafox</div>
                    <div className="text-xs text-[#1E5631]/70">
                      Sourcing Nature. Shipping Purity
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                    Login
                  </h2>
                  <p className="text-sm text-[#1E5631]/70 mt-1">
                    Choose your preferred login method.
                  </p>
                </div>

                <div className="flex gap-2">
                  <span className="px-3 py-1 rounded-full bg-[#1E5631]/8 text-xs border border-[#1E5631]/10">
                    Country: <b>{defaultCountry}</b>
                  </span>
                  <span className="px-3 py-1 rounded-full bg-[#D4AF57]/20 text-xs border border-[#D4AF57]/30">
                    Currency: <b>{currency}</b>
                  </span>
                </div>
              </div>

              {/* Mode Switch */}
              <div className="mt-6 p-1 rounded-2xl bg-[#FAFAF5] border border-[#1E5631]/10 inline-flex w-full">
                <button
                  type="button"
                  onClick={() => {
                    setMode("phone");
                  }}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    mode === "phone"
                      ? "bg-white shadow-sm border border-[#1E5631]/10"
                      : "text-[#1E5631]/70 hover:text-[#1E5631]"
                  }`}
                >
                  Phone OTP
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("email");
                    resetOtpState();
                  }}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    mode === "email"
                      ? "bg-white shadow-sm border border-[#1E5631]/10"
                      : "text-[#1E5631]/70 hover:text-[#1E5631]"
                  }`}
                >
                  Email + Password
                </button>
              </div>

              {/* Forms */}
              <div className="mt-6">
                {mode === "phone" ? (
                  <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
                    <label className="block text-sm font-medium mb-2">
                      Phone number
                    </label>

                    {/* ✅ Modern PhoneInput wrapper */}
                    <div className="rounded-2xl bg-[#FAFAF5] border border-transparent shadow-sm px-3 py-2.5 focus-within:bg-white focus-within:border-[#1E5631]/20 focus-within:ring-2 focus-within:ring-[#1E5631]/15 transition">
                      <PhoneInput
                        key={defaultCountry}
                        international
                        defaultCountry={defaultCountry}
                        value={phone}
                        onChange={(v) => setPhone(v || "")}
                        placeholder="Enter phone number"
                        className="phoneInput"
                      />
                    </div>

                    <div className="mt-2 text-xs text-[#1E5631]/70">
                      Detected country: <b>{defaultCountry}</b>{" "}
                      <span className="mx-2">•</span>
                      {hasUserChosenCurrency ? (
                        <>
                          Your currency: <b>{currency}</b>
                        </>
                      ) : (
                        <>
                          Auto currency: <b>{currency}</b>
                        </>
                      )}
                    </div>

                    {otpSent && (
                      <div className="mt-5">
                        <label className="block text-sm font-medium mb-2">
                          OTP
                        </label>
                        <input
                          value={otp}
                          onChange={(e) => setOtp(onlyDigitsMax6(e.target.value))}
                          className={INPUT}
                          placeholder="Enter 6-digit OTP"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                        />

                        <div className="mt-2 flex items-center justify-between text-xs text-[#1E5631]/70">
                          <span>Enter the 6-digit OTP you received.</span>
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            disabled={loading || cooldown > 0}
                            className="font-semibold underline disabled:opacity-50"
                          >
                            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || (otpSent && onlyDigitsMax6(otp).length !== 6)}
                      className="mt-6 w-full rounded-2xl bg-[#1E5631] text-white py-3 font-semibold hover:bg-[#236e47] transition-all disabled:opacity-60"
                    >
                      {loading
                        ? "Please wait..."
                        : otpSent
                        ? "Verify & Login"
                        : "Send OTP"}
                    </button>

                    {otpSent && (
                      <button
                        type="button"
                        onClick={() => {
                          resetOtpState();
                          setOtp("");
                        }}
                        className="mt-3 w-full rounded-2xl border border-[#1E5631]/20 py-3 font-medium hover:bg-[#FAFAF5] transition-all"
                      >
                        Change number
                      </button>
                    )}
                  </form>
                ) : (
                  <form onSubmit={handleEmailLogin}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={emailForm.email}
                        onChange={(e) =>
                          setEmailForm((p) => ({ ...p, email: e.target.value }))
                        }
                        className={INPUT}
                        placeholder="you@company.com"
                        required
                      />
                    </div>

                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={emailForm.password}
                        onChange={(e) =>
                          setEmailForm((p) => ({
                            ...p,
                            password: e.target.value,
                          }))
                        }
                        className={INPUT}
                        placeholder="••••••••"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="mt-6 w-full rounded-2xl bg-[#1E5631] text-white py-3 font-semibold hover:bg-[#236e47] transition-all disabled:opacity-60"
                    >
                      {loading ? "Logging in..." : "Login"}
                    </button>
                  </form>
                )}
              </div>

              <div className="mt-6 text-sm text-center text-[#1E5631]/80">
                Don&apos;t have an account?{" "}
                <Link to="/register" className="font-semibold underline">
                  Register
                </Link>
              </div>

              <div className="mt-6 text-[12px] text-center text-[#1E5631]/55">
                By continuing, you agree to our Terms & Privacy Policy.
              </div>
            </div>
          </div>

          <div className="hidden lg:block mt-4 text-sm text-[#1E5631]/70">
            <div className="font-semibold">The Nutrafox</div>
            <div className="text-xs">Sourcing Nature. Shipping Purity</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
